/* eslint-disable @typescript-eslint/naming-convention */

import { Component, Inject, Input, OnInit, DOCUMENT, ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef, signal } from '@angular/core';
import { DTypeFormWysiwygField } from '@grupo-constant/next-dtype/form';
import { DTypeViewableCol, WYSIWYGEditor } from '@grupo-constant/next-dtype/types';
import { LangDefinition, TranslocoService } from '@ngneat/transloco';
import { DTypeFormFieldComponent } from '../../dtype-field.model';
import { LocalizePipe } from '../../../pipes/localize.pipe';
import { langDefinition } from '../../../config/app.config';
import { FuseConfigService } from '../../../../../@fuse/services/config/config.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'wysiwyg-field-component',
    templateUrl: './wysiwyg-field.component.html',
    styles: [`
    .invalid {
        border-width: 1px;
        border-style: solid;
        border-color: #Ec2629;
        border-radius: 11px
    }
    `],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DtypeWYSIWYGFieldComponent implements DTypeFormFieldComponent, OnInit, OnDestroy {

    @Input() field: DTypeFormWysiwygField;
    @Input() isUpdate: boolean;
    @Input() useSummaryView: boolean;

    initOptions: any = {
        promotion: false,
        selector: 'textarea',
        language: this.translocoService.getDefaultLang(),
        base_url: '/tinymce',
        suffix: '.min',
        plugins: `lists link charmap emoticons searchreplace code wordcount visualblocks advlist help
            quickbars insertdatetime visualchars fullscreen image table anchor`,
        menubar: 'file edit view insert format tools table help',
        toolbar: `undo redo | bold italic underline strikethrough | fontfamily fontsize blocks |
            forecolor backcolor removeformat | alignleft aligncenter alignright alignjustify |
            outdent indent numlist bullist | charmap emoticons link anchor | fullscreen preview print | code`,
        height: 500,
        contextmenu: 'link image table',
        statusbar: false,
        relative_urls: false,
        convert_urls: false,
        entity_encoding: 'numeric',
        skin: 'oxide',
        content_css: 'default'
    };

    /** LangDefinition as defined in transloco.module */
    availableLangs: LangDefinition[];

    selectedLang: string;
    showEditor = signal(true);

    private unsubscribeAll = new Subject<void>();

    constructor(
        private changeDetector: ChangeDetectorRef,
        @Inject(DOCUMENT) private _document: Document,
        private translocoService: TranslocoService,
        private localize: LocalizePipe,
        private _fuseConfigService: FuseConfigService
    ) { }

    ngOnInit() {
        const readonlyOnUpdate = (this.field.dTypeCol as DTypeViewableCol).readonlyOnUpdate;
        if (readonlyOnUpdate && this.isUpdate) {
            this.field.readonly = true;
        }
        if((this.field.dTypeCol as WYSIWYGEditor).mandatory){
            for (let caption of Object.keys((this.field.dTypeCol as DTypeViewableCol).caption)) {
                caption = caption + ' *';
            }
        }

        this.availableLangs = this.translocoService.getAvailableLangs().map(lang => langDefinition[lang]);
        this.selectedLang = this.translocoService.getActiveLang();

        this._fuseConfigService.config$
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe((conf) => {
                if (conf?.scheme) {
                    if (conf.scheme === 'light') {
                        this.initOptions.skin = 'oxide';
                        this.initOptions.content_css = 'default';
                    } else if (conf.scheme === 'dark') {
                        this.initOptions.skin = 'oxide-dark';
                        this.initOptions.content_css = 'dark';
                    } else if (conf.scheme === 'auto') {
                        if (this._document.body.classList.contains('dark')) {
                            this.initOptions.skin = 'oxide-dark';
                            this.initOptions.content_css = 'dark';
                        } else {
                            this.initOptions.skin = 'oxide';
                            this.initOptions.content_css = 'default';
                        }
                    }
                }
                this.changeDetector.detectChanges();
                this.showEditor.set(false);
                this.changeDetector.detectChanges();

                setTimeout(() => {
                    this.showEditor.set(true);
                    this.changeDetector.detectChanges();
                }, 50);
            });
    }

    ngOnDestroy() {
        this.unsubscribeAll.next();
        this.unsubscribeAll.complete();
    }

}
